"use client"

import { useState } from "react"
import clsx from "clsx"

import type { AllNullable } from "@unlikelystudio/commerce-connector"

import type { PropsWithClassName } from "~/@types/generics"
import type { TVariant } from "~/lib/shopify/serializers/serialize-variants"
import useOptionsTranslate from "~/hooks/useOptionTranslate"
import { OPTION_CONFORT, OPTION_SIZE } from "~/components/ui/Product/ProductHeader/_data/constants"
import {
  getUniqOptions,
  type VariantOptionWithoutColorForSingleProduct,
} from "~/components/ui/Product/ProductHeader/_data/utils/get-uniq-variants-without-color"
import { getVariantConfort } from "~/components/ui/Product/ProductHeader/_data/utils/get-variant-confort"
import { getVariantSize } from "~/components/ui/Product/ProductHeader/_data/utils/get-variant-size"
import RoundedCta from "~/components/ui/RoundedCta"
import Icon from "~/components/abstracts/Icon"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"

import { sprinkles } from "~/styles/sprinkles.css"
import { hideFrom, showFrom } from "~/styles/utils/show-hide"

import * as css from "./styles.css"

type ProductOptionsSelectSelectorProps = PropsWithClassName<{
  options: AllNullable<VariantOptionWithoutColorForSingleProduct>[]
  currentVariant: TVariant | null
  onChange: (option: VariantOptionWithoutColorForSingleProduct) => void
  children?: React.ReactNode
}>

function ProductOptionsSelectSelector({
  className,
  options,
  currentVariant,
  onChange,
  children,
}: ProductOptionsSelectSelectorProps) {
  const t = useTranslate()
  const [isShownMobile, setIsShownMobile] = useState<boolean>(false)

  const sizeOptionsTitle = useOptionsTranslate(OPTION_SIZE)
  const confortOptionsTitle = useOptionsTranslate(OPTION_CONFORT)

  const currentSize = getVariantSize(currentVariant)
  const currentConfort = getVariantConfort(currentVariant)

  const uniqueSizes = getUniqOptions(options, "size")
  const uniqueConforts = getUniqOptions(options, "confort")

  const availableForSaleConfortOption = options.filter((option) => option.size === currentSize)

  const selectsContent = (
    <>
      {currentConfort && uniqueConforts.length > 1 && (
        <div className={css.selectContainer}>
          <span className={clsx(css.title)}>01. {confortOptionsTitle}</span>
          <select
            className={css.select}
            name={"confort-values"}
            id={"confort-values"}
            value={currentConfort}
            onChange={(e) => {
              const currentOption = options.find(
                (current) => current.confort === e.target.value && current.size === currentSize
              )
              currentOption?.id && onChange?.(currentOption)
            }}
          >
            {uniqueConforts?.map((value, index) => {
              if (!value) return null
              const isAvailableForSale = availableForSaleConfortOption[index]?.availableForSale
              return (
                <option value={value} key={`item_confort_option_${index}`}>
                  {value} {!isAvailableForSale && `- ${t("product_out_of_stock_select")}`}
                </option>
              )
            })}
          </select>
          {options && options?.length > 1 && (
            <Icon className={css.ChevronDownIcon} name="ChevronDown" theme="blue-med" width={16} />
          )}
        </div>
      )}

      {currentSize && uniqueSizes.length > 1 && (
        <div className={css.selectContainer}>
          <span className={clsx(css.title)}>02. {sizeOptionsTitle}</span>
          <select
            className={css.select}
            name={"size-values"}
            id={"size-values"}
            value={currentSize}
            onChange={(e) => {
              const currentOption = options.find(
                (current) => current.confort === currentConfort && current.size === e.target.value
              )
              currentOption?.id && onChange?.(currentOption)
            }}
          >
            {uniqueSizes.map((value, index) => {
              if (!value) return null
              return (
                <option value={value} key={`item_size_option_${index}`}>
                  {value}
                </option>
              )
            })}
          </select>
          {options && options?.length > 1 && (
            <Icon className={css.ChevronDownIcon} name="ChevronDown" theme="blue-med" width={16} />
          )}
        </div>
      )}
    </>
  )

  return (
    <>
      <div
        className={clsx(css.ProductSizesSelector, className)}
        data-comp="UI/Product/ProductHeader/ProductDetails/ProductSelector/ProductOptionsSelectSelector"
      >
        {/* Desktop content */}
        <div className={clsx(showFrom("tablet", "block"))}>{selectsContent}</div>

        {/* Tablet content, thanks Numbered */}
        <div className={clsx(hideFrom("tablet", "block"))}>
          {isShownMobile && selectsContent}
          {!isShownMobile && (
            <div className={sprinkles({ mX: { mobile: 10, desktop: 0 } })}>
              <RoundedCta
                size="large"
                onClick={() => {
                  setIsShownMobile(true)
                }}
                width="full"
                theme="blue-deep"
              >
                {t("product_select_sizes")}
              </RoundedCta>
            </div>
          )}
        </div>
      </div>
      <div className={clsx(showFrom("tablet", "block"))}>{children}</div>
      {isShownMobile && <div className={clsx(hideFrom("tablet", "block"))}>{children}</div>}
    </>
  )
}

export { ProductOptionsSelectSelector, type ProductOptionsSelectSelectorProps as ProductSizesSelectorProps }
